import type {
  OfficialStatusProvidersResponse,
  PublicStatusResponse,
  TenantCodexCapacityResponse,
} from '@nadoutong/contracts';
import type { UseQueryResult } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  getCodexCapacity,
  getNxtpathPublicStatus,
  getOfficialProviderStatus,
  getPlatformHealth,
} from './status.ts';

export const NXTPATH_STATUS_QUERY_KEY = ['status', 'nxtpath'] as const;
export const PLATFORM_HEALTH_QUERY_KEY = ['status', 'platform-health'] as const;
export const CODEX_CAPACITY_QUERY_KEY = ['status', 'codex-capacity'] as const;
export const OFFICIAL_STATUS_QUERY_KEY = ['status', 'official-providers'] as const;

/** Backend refreshes the archive every 5 minutes; do not poll faster. */
const CODEX_CAPACITY_REFRESH_MS = 5 * 60_000;
/** Backend refreshes the official status mirror about once a minute. */
const OFFICIAL_STATUS_REFRESH_MS = 60_000;

export function useNxtpathPublicStatus(): UseQueryResult<PublicStatusResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: NXTPATH_STATUS_QUERY_KEY,
    queryFn: ({ signal }) => getNxtpathPublicStatus(fetchImpl, signal),
    staleTime: 120_000,
  });
}

export function usePlatformHealth(): UseQueryResult<{ readonly ok: true }> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: PLATFORM_HEALTH_QUERY_KEY,
    queryFn: ({ signal }) => getPlatformHealth(fetchImpl, signal),
    staleTime: 120_000,
  });
}

/**
 * Codex at-capacity series for `/status`.
 *
 * The page overlays this onto Codex availability rows only when `data`
 * is present. A 404, any other error, or an in-flight request leaves
 * the timelines untouched — no error panel and no placeholder. 401 is
 * left to the session layer, same as every other page.
 */
export function useCodexCapacity(): UseQueryResult<TenantCodexCapacityResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: CODEX_CAPACITY_QUERY_KEY,
    queryFn: ({ signal }) => getCodexCapacity(fetchImpl, signal),
    staleTime: CODEX_CAPACITY_REFRESH_MS,
    refetchInterval: CODEX_CAPACITY_REFRESH_MS,
    retry: false,
  });
}

/**
 * Official provider status for the bottom of `/status`.
 *
 * The panel renders on its own: a loading request shows a skeleton, and
 * a 404 (production until the route ships), network error, or any other
 * failure renders nothing — no error panel. 401 is left to the session
 * layer, same as {@link useCodexCapacity}.
 */
export function useOfficialProviderStatus(): UseQueryResult<OfficialStatusProvidersResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: OFFICIAL_STATUS_QUERY_KEY,
    queryFn: ({ signal }) => getOfficialProviderStatus(fetchImpl, signal),
    staleTime: OFFICIAL_STATUS_REFRESH_MS,
    refetchInterval: OFFICIAL_STATUS_REFRESH_MS,
    retry: false,
  });
}
